import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Check, X, GripVertical } from "lucide-react";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useReorderMenuCategories } from "@/lib/menuOrder";
import { SortableArea, useSortableItem } from "@/components/Sortable";
import { Button, EmptyState, Field, Input, Loading, Page, PageHeader } from "@/components/kit";

const CFG = {
  categories: { title: "Categorias", single: "Categoria", label: "Nova categoria", ph: "Ex.: Carnes, Hortifruti, Bebidas", empty: "Sem categorias", text: "Crie categorias para organizar seus produtos e ver o CMV por grupo.", id: "category", m: "a" },
  sectors: { title: "Setores", single: "Setor", label: "Novo setor", ph: "Ex.: Cozinha, Bar, Câmara fria", empty: "Sem setores", text: "Crie setores para dividir a contagem entre os operadores.", id: "sector", m: "o" },
  "menu-categories": { title: "Categorias de fichas técnicas", single: "Categoria", label: "Nova categoria do cardápio", ph: "Ex.: Pizzas, Bebidas, Sobremesas", empty: "Sem categorias", text: "Crie categorias para organizar as fichas técnicas do cardápio.", id: "menu-category", m: "a", sortable: true, count: (r) => (r.recipes === 1 ? "1 ficha técnica" : `${r.recipes} fichas técnicas`) },
};
const countText = (cfg, r) => (cfg.count ? cfg.count(r) : `${r.products} produtos`);

function Row({ r, cfg, kind, onDone, sortable }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(r.name);
  const s = useSortableItem(r.id, !sortable || edit);
  const upd = useMutation({ mutationFn: () => api(`/${kind}/${r.id}`, { method: "PUT", body: { name } }), onSuccess: () => { setEdit(false); onDone(`${cfg.single} atualizad${cfg.m}`); }, onError: (e) => toast.error(e.message) });
  const del = useMutation({ mutationFn: () => api(`/${kind}/${r.id}`, { method: "DELETE" }), onSuccess: () => onDone(`${cfg.single} excluíd${cfg.m}`), onError: (e) => toast.error(e.message) });
  return (
    <div ref={s.ref} className="flex items-center gap-3" style={{ padding: "12px var(--pad)", ...s.style, ...(s.isDragging ? { background: "var(--surface-alt)", position: "relative" } : {}) }} data-testid={`${cfg.id}-row-${r.id}`}>
      {sortable && <button type="button" className="icon-btn cursor-grab" style={{ touchAction: "none" }} title="Arraste para ordenar" {...s.handle} data-testid={`drag-${cfg.id}-${r.id}`}><GripVertical size={16} /></button>}
      {edit ? (
        <>
          <Input autoFocus className="input-sm flex-1" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && upd.mutate()} data-testid={`edit-${cfg.id}-input-${r.id}`} />
          <button className="icon-btn" onClick={() => upd.mutate()} data-testid={`save-${cfg.id}-${r.id}`}><Check size={16} /></button>
          <button className="icon-btn" onClick={() => { setEdit(false); setName(r.name); }} data-testid={`cancel-edit-${cfg.id}-${r.id}`}><X size={16} /></button>
        </>
      ) : (
        <>
          <div className="flex-1"><div className="h3">{r.name}</div><div className="caption">{countText(cfg, r)}</div></div>
          <button className="icon-btn" onClick={() => setEdit(true)} data-testid={`edit-${cfg.id}-${r.id}`}><Pencil size={15} /></button>
          <button className="icon-btn" onClick={() => del.mutate()} data-testid={`delete-${cfg.id}-${r.id}`}><Trash2 size={15} /></button>
        </>
      )}
    </div>
  );
}

export default function NamedList({ kind }) {
  const cfg = CFG[kind];
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: [kind], queryFn: () => api(`/${kind}`) });
  const reorder = useReorderMenuCategories();
  const done = (msg) => { [kind, "products", "recipes"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success(msg); };
  const add = useMutation({ mutationFn: () => api(`/${kind}`, { method: "POST", body: { name } }), onSuccess: () => { setName(""); done(`${cfg.single} criad${cfg.m}`); }, onError: (e) => toast.error(e.message) });
  return (
    <Page narrow>
      <PageHeader title={cfg.title} back />
      <div className="flex gap-3 items-end mb-5">
        <div className="flex-1"><Field label={cfg.label}><Input placeholder={cfg.ph} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && add.mutate()} data-testid={`new-${cfg.id}-input`} /></Field></div>
        <Button disabled={!name.trim()} loading={add.isPending} onClick={() => add.mutate()} data-testid={`add-${cfg.id}-button`}>Adicionar</Button>
      </div>
      {isLoading ? <Loading /> : !data.length ? <EmptyState title={cfg.empty} text={cfg.text} /> : (
        <>
          {cfg.sortable && <p className="caption mb-2" data-testid="menu-order-hint">Arraste pelo ícone para definir a ordem do cardápio na lista de fichas e no PDF.</p>}
          <SortableArea items={data} strategy={verticalListSortingStrategy} onReorder={(rows) => reorder.mutate(rows)}>
            <div className="card divide" style={{ padding: 0 }}>{data.map((r) => <Row key={r.id} r={r} cfg={cfg} kind={kind} onDone={done} sortable={cfg.sortable} />)}</div>
          </SortableArea>
        </>
      )}
    </Page>
  );
}
